import { notEmpty, type NodeV2 } from '@vedalib/editor'
import cn from 'classnames'
import React, { useCallback, useEffect, useRef } from 'react'

import { useProjectContext } from 'services/Store/Project/hooks'
import { getNodeStyle, mergeNodeStyle } from 'services/Store/Project/selectors'
import type { Block, IBlockMode } from 'services/Store/Project/types'

import { findNodeRef } from '../DisplayProvider/utils'
import { onDrag } from '../commands/onDrag'
import { applyArea, safeParseArea } from '../formControls/Area/utils'
import { safeParseGrid } from '../formControls/Grid/utils'
import { safeParsePxSizes } from '../formControls/Sizes/utils'
import * as s from './Controls.module.scss'

interface INodeV2GroupSelectionProps {
  children?: React.ReactNode
  mode: IBlockMode
  block: Block
  node: NodeV2
  nodeRef: React.RefObject<HTMLDivElement>
  selected: NodeV2[]
}

const NodeV2GroupSelection: React.FC<INodeV2GroupSelectionProps> = ({ children, ...props }) => {
  const { mode, block, node, nodeRef, selected } = props

  const ref = useRef<HTMLDivElement>(null)

  const parentStyles = useProjectContext(getNodeStyle, block.uuid, node.id)
  const isRepeatable =
    node.repeatable || parentStyles?.display === 'flex' || parentStyles?.display === 'absolute'

  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      onDrag(event, { mode: mode.editorMode, blockId: block.uuid, source: node, targets: selected })
    },
    [mode.editorMode, block.uuid, node, selected],
  )

  const update = useCallback(() => {
    requestAnimationFrame(() => {
      if (!isRepeatable) {
        const styles = selected.map((node) =>
          mergeNodeStyle(
            node.style,
            mode.deviceMode,
            node.elementId ? block.elements[node.elementId]?.type : undefined,
          ),
        )

        const areas =
          selected.length > 0
            ? styles.map((style) => safeParseArea(style.gridArea))
            : [
                {
                  rowStart: 0,
                  rowEnd: safeParseGrid(parentStyles?.gridRows).items.length + 1,
                  colStart: 0,
                  colEnd: safeParseGrid(parentStyles?.gridColumns).items.length + 1,
                },
              ]

        const rowStart = Math.min(...areas.map((area) => area.rowStart)) || 0
        const colStart = Math.min(...areas.map((area) => area.colStart)) || 0
        const rowEnd = Math.max(...areas.map((area) => area.rowEnd)) || 0
        const colEnd = Math.max(...areas.map((area) => area.colEnd)) || 0

        if (ref.current) {
          ref.current.style.display = 'block'
          ref.current.style.gridArea = applyArea(parentStyles, {
            rowStart,
            rowEnd,
            colStart,
            colEnd,
          })
        }
      } else {
        const refs = selected
          .map((node) => {
            const ref = findNodeRef(node.id)
            const style = mergeNodeStyle(node.style, mode.deviceMode)
            return ref ? { ref, style } : null
          })
          .filter(notEmpty)
        const parentBox = nodeRef.current?.getBoundingClientRect() || {
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }
        const boxes = refs.map(({ ref, style }) => {
          const margin = safeParsePxSizes(style.margin)
          const box = ref.getBoundingClientRect()
          const [topMargin, rightMargin, bottomMargin, leftMargin] = margin
          return {
            top: box.top - (topMargin || 0),
            left: box.left - (leftMargin || 0),
            right: box.right + (rightMargin || 0),
            bottom: box.bottom + (bottomMargin || 0),
          }
        })
        const top = Math.min(...boxes.map((box) => box.top)) - parentBox.top
        const left = Math.min(...boxes.map((box) => box.left)) - parentBox.left
        const right = parentBox.right - Math.max(...boxes.map((box) => box.right))
        const bottom = parentBox.bottom - Math.max(...boxes.map((box) => box.bottom))
        const refStyles = { top, left, right, bottom }
        if (ref.current) {
          ref.current.style.display = 'block'
          ref.current.style.position = 'absolute'
          ref.current.style.top = `${refStyles.top}px`
          ref.current.style.bottom = `${refStyles.bottom}px`
          ref.current.style.left = `${refStyles.left}px`
          ref.current.style.right = `${refStyles.right}px`
        }
      }
    })
  }, [block.elements, isRepeatable, mode.deviceMode, nodeRef, selected, parentStyles])

  useEffect(() => {
    const refs = selected.map((node) => findNodeRef(node.id)).filter(Boolean) as HTMLDivElement[]
    update()
    const resizeObserver = new ResizeObserver(update)
    const observer = new MutationObserver(update)
    refs.forEach((ref) => resizeObserver.observe(ref))
    refs.forEach((ref) => observer.observe(ref, { attributes: true }))

    return () => {
      resizeObserver.disconnect()
      observer.disconnect()
    }
  }, [update, selected, nodeRef])

  useEffect(() => {
    update()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div
      className={cn(s.wrapper, s[parentStyles?.display || 'element'], {
        [s.group]: selected.length > 1,
      })}
      id={node.id}
      onMouseDown={handleMouseDown}
      ref={ref}
    >
      {children}
    </div>
  )
}

export default React.memo(NodeV2GroupSelection)
